import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteIcon,
  PhoneIcon,
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { motion } from "framer-motion";
import { MdPeople } from "react-icons/md";
import useAxios from "axios-hooks";

// Motion components
const MotionBox = motion(Box);
const MotionTr = motion(Tr);

// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const StatusBadge = ({ status }) => {
  let color, icon, label;

  switch (status) {
    case true:
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
      break;
    case false:
      color = "red";
      icon = WarningIcon;
      label = "Inactive";
      break;
    default:
      color = "gray";
      icon = InfoIcon;
      label = "Unknown";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const ProfileUsers = ({ pData }) => {
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [newMobileNumbers, setNewMobileNumbers] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const toast = useToast();

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "#f57d36";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");

  const [{ data: userData, loading, error }, execute] = useAxios(
    { url: `/api/userprofiles/${pData?.Id}/users`, method: "GET" },
    { manual: true }
  );
  const [{ data: addData, loading: addLoading, error: addError }, AddUsers] =
    useAxios(
      { url: `/api/userprofiles/${pData?.Id}/users`, method: "POST" },
      { manual: true }
    );

  const [
    { data: deleteData, loading: deleteLoading, error: deleteError },
    DeleteUsers,
  ] = useAxios(
    { url: `/api/userprofiles/${pData?.Id}/users`, method: "DELETE" },
    { manual: true }
  );
  const fetchUsers = async () => {
    try {
      const response = await execute();
      setMobileNumbers(response.data.data);
    } catch (err) {
      if (error?.code === "ERR_CANCELED") return;
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    if (pData?.Id) {
      fetchUsers();
    }
  }, [pData?.Id]);

  // Mask mobile number to show only last 4 digits
  const maskMobileNumber = (number) => {
    if (!number || number.length < 4) return number;
    return "******" + number.slice(-4);
  };

  // Validate mobile number (10 digits)
  const validateMobileNumber = (number) => {
    const cleaned = number.trim();
    return /^[0-9]{10}$/.test(cleaned);
  };

  // Add mobile numbers
  const handleAddMobileNumbers = async () => {
    // Clear any existing selections first
    setSelectedRows([]);

    if (!newMobileNumbers.trim()) {
      toast({
        title: "Please enter mobile numbers",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const numbers = newMobileNumbers
      .split(",")
      .map((num) => num.trim())
      .filter((num) => num);

    const validNumbers = [];
    const invalidNumbers = [];

    numbers.forEach((num) => {
      if (validateMobileNumber(num)) {
        // Check if number already exists
        const exists = mobileNumbers.some((item) => item.MobileNumber === num);
        if (!exists) {
          validNumbers.push(num);
        }
      } else {
        invalidNumbers.push(num);
      }
    });

    if (invalidNumbers.length > 0) {
      toast({
        title: "Invalid mobile numbers",
        description: `These numbers are invalid: ${invalidNumbers.join(", ")}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (validNumbers.length > 0) {
      try {
        // Send data in ProfileUsersDTO format
        await AddUsers({
          data: {
            Additions: validNumbers,
            Removals: [],
          },
        });

        toast({
          title: `Added ${validNumbers.length} mobile number(s)`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        setNewMobileNumbers("");
        fetchUsers(); // Refresh the list
      } catch (error) {
        console.error("Error adding users:", error);
        toast({
          title: "Error adding mobile numbers",
          description: error?.response?.data?.error || "Please try again",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Delete selected mobile numbers
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      toast({
        title: "No numbers selected",
        description: "Please select mobile numbers to delete",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // Get mobile numbers to delete
      const numbersToDelete = mobileNumbers
        .filter((item) => selectedRows.includes(item.Id))
        .map((item) => item.MobileNumber);

      // Send data in ProfileUsersDTO format
      await DeleteUsers({
        data: {
          Additions: [],
          Removals: numbersToDelete,
        },
      });

      toast({
        title: `Deleted ${selectedRows.length} mobile number(s)`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setSelectedRows([]);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error("Error deleting users:", error);
      toast({
        title: "Error deleting mobile numbers",
        description: error?.response?.data?.error || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Delete single mobile number
  const handleDeleteSingle = async (id) => {
    try {
      // Find the mobile number to delete
      const userToDelete = mobileNumbers.find((item) => item.Id === id);
      if (!userToDelete) return;

      // Send data in ProfileUsersDTO format
      await DeleteUsers({
        data: {
          Additions: [],
          Removals: [userToDelete.MobileNumber],
        },
      });

      toast({
        title: "Mobile number deleted",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error("Error deleting user:", error);
      toast({
        title: "Error deleting mobile number",
        description: error?.response?.data?.error || "Please try again",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Toggle row selection
  const toggleRowSelection = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  // Toggle all rows selection
  const toggleAllRows = () => {
    if (selectedRows.length === filteredData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((item) => item.Id));
    }
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return mobileNumbers;

    return mobileNumbers.filter((item) =>
      item.MobileNumber.includes(searchQuery.trim())
    );
  }, [mobileNumbers, searchQuery]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "selectAndSno",
      },
      {
        Header: "Mobile Number",
        accessor: "mobileNumber",
      },
      {
        Header: "Added Date",
        accessor: "addedAt",
      },
      {
        Header: "Actions",
        accessor: "actions",
      },
    ],
    []
  );

  const data = useMemo(() => filteredData, [filteredData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = tableInstance;

  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      {/* Profile Info Header */}
      <GlassMorphismCard p={6} mb={6}>
        {/* Add Mobile Numbers Section */}
        <VStack align="stretch" spacing={4}>
          <Textarea
            placeholder="e.g., 1234567890, 9812345678, 1234567980"
            value={newMobileNumbers}
            onChange={(e) => setNewMobileNumbers(e.target.value)}
            rows={3}
            focusBorderColor={themeColor}
            borderRadius="md"
          />
          <Text fontSize="sm" color="gray.500">
            Enter one or more mobile numbers separated by commas (10 digits
            each). Please enter numbers without country code
          </Text>
          <Flex gap={3}>
            <Button
              // leftIcon={<AddIcon />}
              bg={themeColor}
              color="white"
              colorScheme="orange"
              onClick={handleAddMobileNumbers}
              _hover={{
                bg: "rgba(245, 125, 54, 0.9)",
                transform: "translateY(-2px)",
              }}
            >
              Add Users
            </Button>
            <Button
              variant="outline"
              onClick={() => setNewMobileNumbers("")}
              borderColor={themeColor}
              color={themeColor}
              _hover={{
                bg: "rgba(245, 125, 54, 0.1)",
              }}
            >
              Clear
            </Button>
          </Flex>
        </VStack>
      </GlassMorphismCard>

      {/* Mobile Numbers List */}
      <GlassMorphismCard p={0}>
        {/* Header with search and delete */}
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <VStack align="start" spacing={2} flex={1}>
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Mobile Numbers
              </Text>
              <InputGroup size="sm" maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search by mobile number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="md"
                  _focus={{
                    borderColor: themeColor,
                    boxShadow: `0 0 0 1px ${themeColor}`,
                  }}
                />
              </InputGroup>
            </VStack>

            <Flex align="center" gap={4}>
              {selectedRows.length > 0 && (
                <Button
                  leftIcon={<DeleteIcon />}
                  colorScheme="red"
                  variant="solid"
                  size="sm"
                  onClick={handleDeleteSelected}
                >
                  Delete Selected ({selectedRows.length})
                </Button>
              )}
              <Flex align="center" gap={2}>
                <Icon as={MdPeople} color={themeColor} boxSize={5} />
                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                  Mobile Numbers
                </Text>
                <Badge colorScheme="orange" borderRadius="full" px={2}>
                  {mobileNumbers.length} total
                </Badge>
              </Flex>
            </Flex>
          </Flex>
        </Box>

        {/* Table */}
        {mobileNumbers.length > 0 ? (
          <>
            <TableContainer>
              <Table {...getTableProps()} variant="simple" colorScheme="gray">
                <Thead bg={theadBgColor}>
                  {headerGroups.map((headerGroup, index) => (
                    <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                      {headerGroup.headers.map((column, index) => (
                        <Th
                          {...column.getHeaderProps(
                            column.id !== "selectAndSno" &&
                              column.id !== "actions"
                              ? column.getSortByToggleProps()
                              : {}
                          )}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          textTransform="uppercase"
                          fontSize="xs"
                          fontWeight="bold"
                          letterSpacing="wider"
                          color="gray.500"
                          textAlign={
                            column.id === "selectAndSno" ? "left" : "center"
                          }
                          cursor={
                            column.id !== "selectAndSno" &&
                            column.id !== "actions"
                              ? "pointer"
                              : "default"
                          }
                          _hover={
                            column.id !== "selectAndSno" &&
                            column.id !== "actions"
                              ? { color: themeColor }
                              : {}
                          }
                          transition="all 0.2s"
                        >
                          <Flex
                            justify={
                              column.id === "selectAndSno"
                                ? "flex-start"
                                : "center"
                            }
                            align="center"
                            gap={3}
                          >
                            {column.id === "selectAndSno" ? (
                              <>
                                <Checkbox
                                  isChecked={
                                    selectedRows.length ===
                                      filteredData.length &&
                                    filteredData.length > 0
                                  }
                                  isIndeterminate={
                                    selectedRows.length > 0 &&
                                    selectedRows.length < filteredData.length
                                  }
                                  onChange={toggleAllRows}
                                  colorScheme="orange"
                                />
                                <Text>S.No</Text>
                              </>
                            ) : (
                              <>
                                {column.render("Header")}
                                {column.id !== "actions" &&
                                  column.render("Header") && (
                                    <Box>
                                      {column.isSorted ? (
                                        column.isSortedDesc ? (
                                          <Icon as={FaSortDown} />
                                        ) : (
                                          <Icon as={FaSortUp} />
                                        )
                                      ) : (
                                        <Icon as={FaSort} opacity={0.3} />
                                      )}
                                    </Box>
                                  )}
                              </>
                            )}
                          </Flex>
                        </Th>
                      ))}
                    </Tr>
                  ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                  {page.map((row, index) => {
                    prepareRow(row);
                    return (
                      <MotionTr
                        {...row.getRowProps()}
                        key={index}
                        whileHover={{ backgroundColor: hoverBg }}
                        transition={{ duration: 0.2 }}
                        _hover={{ bg: hoverBg }}
                      >
                        {row.cells.map((cell, index) => {
                          let data = "";
                          if (cell.column.id === "selectAndSno") {
                            data = (
                              <Flex align="center" justify="flex-start" gap={3}>
                                <Checkbox
                                  isChecked={selectedRows.includes(
                                    cell.row.original.Id
                                  )}
                                  onChange={() =>
                                    toggleRowSelection(cell.row.original.Id)
                                  }
                                  colorScheme="orange"
                                />
                                <Text
                                  color={textColor}
                                  fontSize="sm"
                                  fontWeight="600"
                                  minW="30px"
                                >
                                  {pageIndex * pageSize +
                                    Number(cell.row.id) +
                                    1}
                                </Text>
                              </Flex>
                            );
                          } else if (cell.column.id === "mobileNumber") {
                            data = (
                              <Flex align="center" justify="center" gap={2}>
                                <Icon as={PhoneIcon} color={themeColor} />
                                <Text
                                  color={textColor}
                                  fontSize="sm"
                                  fontWeight="600"
                                  fontFamily="mono"
                                >
                                  {maskMobileNumber(
                                    cell.row.original.MobileNumber
                                  )}
                                </Text>
                              </Flex>
                            );
                          } else if (cell.column.id === "addedAt") {
                            const formatDateTime = (dateTimeStr) => {
                              if (!dateTimeStr) return "-";
                              const date = new Date(dateTimeStr);
                              return date.toLocaleString("en-IN", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                              });
                            };
                            data = (
                              <Flex align="center" justify="center">
                                <Text color="gray.500" fontSize="sm">
                                  {formatDateTime(cell.row.original.UpdatedAt)}
                                </Text>
                              </Flex>
                            );
                          } else if (cell.column.id === "actions") {
                            data = (
                              <Flex align="center" justify="center" gap={2}>
                                <Tooltip
                                  label="Delete Number"
                                  placement="top"
                                  hasArrow
                                >
                                  <IconButton
                                    icon={<DeleteIcon />}
                                    colorScheme="red"
                                    variant="ghost"
                                    size="sm"
                                    borderRadius="full"
                                    aria-label="Delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSingle(cell.row.original.Id);
                                    }}
                                  />
                                </Tooltip>
                              </Flex>
                            );
                          }
                          return (
                            <Td
                              {...cell.getCellProps()}
                              key={index}
                              py={4}
                              px={6}
                              borderColor={borderColor}
                              fontSize="sm"
                            >
                              {data}
                            </Td>
                          );
                        })}
                      </MotionTr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Flex
              p={4}
              justify="space-between"
              align="center"
              borderTop="1px solid"
              borderColor={borderColor}
              flexWrap="wrap"
              gap={4}
            >
              <HStack>
                <Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  size="sm"
                  borderRadius="md"
                  w="120px"
                >
                  {[5, 10, 20, 30, 40, 50].map((size) => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </Select>
                <Text fontSize="sm" color="gray.500">
                  Showing {page.length} of {filteredData.length} results
                </Text>
              </HStack>

              <HStack spacing={2}>
                <PaginationButton
                  isDisabled={!canPreviousPage}
                  onClick={() => gotoPage(0)}
                >
                  <ChevronLeftIcon boxSize={4} mr={1} />
                  First
                </PaginationButton>

                <PaginationButton
                  isDisabled={!canPreviousPage}
                  onClick={() => previousPage()}
                >
                  <ChevronLeftIcon boxSize={4} />
                </PaginationButton>

                {pageCount <= 5 ? (
                  [...Array(pageCount)].map((_, i) => (
                    <PaginationButton
                      key={i}
                      isActive={pageIndex === i}
                      onClick={() => gotoPage(i)}
                    >
                      {i + 1}
                    </PaginationButton>
                  ))
                ) : (
                  <>
                    {pageIndex > 1 && (
                      <PaginationButton onClick={() => gotoPage(0)}>
                        1
                      </PaginationButton>
                    )}

                    {pageIndex > 2 && <Text>...</Text>}

                    {pageIndex > 0 && (
                      <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                        {pageIndex}
                      </PaginationButton>
                    )}

                    <PaginationButton
                      isActive
                      onClick={() => gotoPage(pageIndex)}
                    >
                      {pageIndex + 1}
                    </PaginationButton>

                    {pageIndex < pageCount - 1 && (
                      <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                        {pageIndex + 2}
                      </PaginationButton>
                    )}

                    {pageIndex < pageCount - 3 && <Text>...</Text>}

                    {pageIndex < pageCount - 2 && (
                      <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                        {pageCount}
                      </PaginationButton>
                    )}
                  </>
                )}

                <PaginationButton
                  isDisabled={!canNextPage}
                  onClick={() => nextPage()}
                >
                  <ChevronRightIcon boxSize={4} />
                </PaginationButton>

                <PaginationButton
                  isDisabled={!canNextPage}
                  onClick={() => gotoPage(pageCount - 1)}
                >
                  Last
                  <ChevronRightIcon boxSize={4} ml={1} />
                </PaginationButton>
              </HStack>
            </Flex>
          </>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            py={12}
            px={6}
          >
            <Icon as={MdPeople} boxSize={16} color={themeColor} mb={4} />
            <Text fontSize="xl" fontWeight="bold" mb={2}>
              No Mobile Numbers Added
            </Text>
            <Text color="gray.500" textAlign="center" maxW="md">
              Start by adding mobile numbers using the form above. You can add
              multiple numbers at once.
            </Text>
          </Flex>
        )}
      </GlassMorphismCard>
    </MotionBox>
  );
};

export default ProfileUsers;
